"use client";
import { useEffect, useState } from "react";
import axios from "axios";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { z } from "zod";
import dynamic from "next/dynamic";
import { createPortal } from "react-dom";
import "react-phone-input-2/lib/style.css";
const PhoneInput = dynamic(() => import("react-phone-input-2"), { ssr: false });
import { useRouter } from "next/navigation";
import { useCurrentLocale } from "@/utils/hooks/useCurrentLocale";
import { localeText } from "@/utils/constantTxt/localeText";

export interface ModalProps {
  btnMeta?: {
    tag: string;
  };
  color?: "green" | "blue" | "violet";
  closeForm: () => void;
  showDate?: boolean;
}
type FormFields = {
  name_modal: string;
  phone_modal: string;
  date_modal?: string;
};

export default function ModalForm({
  btnMeta,
  closeForm,
  color = "green",
  showDate = true,
}: ModalProps) {
  const loc = useCurrentLocale();

  const formSchema = z.object({
    name_modal: z
      .string()
      .trim()
      .min(1, localeText[loc].nameRequired)
      .max(100, localeText[loc].nameTooLong),
    countryCode: z.string().trim(),
    phone_modal: z
      .string()
      .min(1, localeText[loc].phoneRequired)
      .max(15, localeText[loc].phoneTooLong)
      .regex(/^\+?[0-9]{12,14}$/, localeText[loc].phoneWrong),
    date_modal: z.string().optional(),
    tag: z.string().optional(),
  });

  const [formData, setFormData] = useState({
    name_modal: "",
    phone_modal: "",
    date_modal: "",
    countryCode: "UA",
    tag: btnMeta?.tag || "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    name_modal: "",
    phone_modal: "",
    date_modal: "",
  });
  const [success, setSuccess] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const handlePhoneChange = (
    value: string,
    country: { countryCode: string }
  ) => {
    setFormData({
      ...formData,
      phone_modal: value,
      countryCode: country.countryCode,
    });
    setErrors({ ...errors, phone_modal: "" });
  };

  const router = useRouter();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors({ name_modal: "", phone_modal: "", date_modal: "" });
    const validationResult = formSchema.safeParse(formData);
    if (!validationResult.success) {
      const newErrors: FormFields = {
        name_modal: "",
        phone_modal: "",
        date_modal: "",
      };

      validationResult.error.errors.forEach((curr) => {
        const fieldName = curr.path[0] as keyof FormFields; // Вказуємо тип
        newErrors[fieldName] = curr.message;
      });

      setErrors({ date_modal: "", ...newErrors });
      setLoading(false);
      return;
    }

    const utmTags = JSON.parse(localStorage.getItem("utmTags") || "{}");

    try {
      await axios.post("https://ap-server-8qi1.onrender.com/leads", {
        tag: validationResult.data.tag,
        name: validationResult.data.name_modal,
        phone: validationResult.data.phone_modal,
        time: validationResult.data.date_modal,
        ...utmTags,
      });
      setSuccess(true);
      router.push("/thankyou");
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        phone_modal: localeText[loc].formError,
      }));
    } finally {
      setLoading(false);
    }
  };
  const backgroundColorClass =
    {
      green: "custom-gradient-green-btn",
      blue: "custom-gradient-blue-btn",
      violet: "custom-gradient-violet-btn",
    }[color] || "custom-gradient-green-btn";
  const borderColorClass =
    {
      green: "border-green-500",
      blue: "border-blue-500",
      violet: "border-violet-500",
    }[color] || "border-green-500";
  const textColorClass =
    {
      green: "text-green-500",
      blue: "text-blue-500",
      violet: "text-violet-500",
    }[color] || "text-green-500";
  if (!mounted) {
    return null;
  }

  return createPortal(
    <div
      data-meta="ModalForm"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black px-4 py-8"
      onClick={closeForm}
    >
      <div
        className="relative w-full max-w-md rounded-3xl border border-white/10 bg-white p-6 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          onClick={closeForm}
          aria-label="Close form"
          className={`absolute right-4 top-4 rounded-full border ${borderColorClass} p-2`}
          type="button"
        >
          <XMarkIcon className={`h-4 w-4 ${textColorClass}`} />
        </button>
        <div className="flex flex-col gap-6">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              {localeText[loc].needConsultation}
            </p>
            <p className="text-sm text-gray-500">{localeText[loc].leaveRequest}</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="name-modal"
                className="sr-only block text-sm font-medium text-gray-700"
              >
                {localeText[loc].name}
              </label>
              <input
                type="text"
                name="name_modal"
                id="name-modal"
                placeholder={localeText[loc].name}
                autoComplete="off"
                className={`mt-1 block w-full px-4 py-2.5 border border-gray-300 rounded-full focus:shadow-md focus:border-gray-300 focus:ring-0  sm:text-sm`}
                value={formData.name_modal}
                onChange={handleChange}
                required
              />
              {errors.name_modal && (
                <p className="mt-1 text-red-500 text-sm">{errors.name_modal}</p>
              )}
            </div>
            <div>
              <label
                htmlFor="phone-modal"
                className="sr-only block text-sm font-medium text-gray-700"
              >
                {localeText[loc].phone}
              </label>
              <PhoneInput
                country={"ua"}
                excludeCountries={["ru"]}
                value={formData.phone_modal}
                placeholder={localeText[loc].phone}
                onChange={handlePhoneChange}
                containerClass={`mt-1 block w-full`}
                inputClass={`w-full pl-12 pr-3 py-2 rounded-full border ${
                  errors.phone_modal ? "border-red-500" : "border-gray-300"
                }  sm:text-sm focus:shadow-md focus:border-gray-300 focus:ring-0`}
                buttonClass="hover:bg-inherit focus:bg-inherit bg-inherit border-l-0 border-gray-300"
                inputProps={{
                  name: "phone_modal",
                  id: "phone-modal",
                  required: true,
                }}
                containerStyle={{
                  borderColor: "#fff",
                  width: "100%",
                }}
                inputStyle={{
                  width: "100%",
                  height: "2.5rem",
                  fontSize: "0.875rem",
                  backgroundColor: "#fff",
                  borderRadius: "2rem",
                }}
                buttonStyle={{
                  backgroundColor: "inherit",
                  borderRadius: "2rem 0 0 2rem",
                  borderWidth: "1px",
                  borderColor: errors.phone_modal ? "#ef4444" : "#e2e8f0",
                }}
              />
              {errors.phone_modal && (
                <p className="mt-1 text-red-500 text-sm">{errors.phone_modal}</p>
              )}
            </div>
            {showDate && (
              <div>
                <label
                  htmlFor="date"
                  className="sr-only block text-sm font-medium text-gray-700"
                >
                  {localeText[loc].whenCalling}
                </label>
                <input
                  type="text"
                  name="date_modal"
                  id="date"
                  placeholder={localeText[loc].whenCalling}
                  autoComplete="off"
                  className={`mt-1 block w-full px-4 py-2.5 border border-gray-300 rounded-full focus:shadow-md focus:border-gray-300 focus:ring-0  sm:text-sm`}
                  value={formData.date_modal}
                  onChange={handleChange}
                />
                {errors.date_modal && (
                  <p className="mt-1 text-red-500 text-sm">{errors.date_modal}</p>
                )}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className={`${backgroundColorClass} uppercase w-full text-white rounded-full px-8 py-2.5 text-center text-sm font-fixel font-bold shadow-sm focus-visible:outline`}
            >
              {loading ? localeText[loc].sending : localeText[loc].send}
            </button>
            {success && (
              <p className="text-center text-green-500 text-sm">
                {localeText[loc].formSuccsess}
              </p>
            )}
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}
